/**
 * WebMCP: tarayıcıdaki yapay zekâ ajanlarına sitenin ana işlemlerini araç
 * olarak açar (https://webmachinelearning.github.io/webmcp/).
 *
 * Araçlar kullanıcının kendi oturumunda, sayfanın içinde çalışır: ajan
 * hikâye arar, açar, okur ya da bir konuya gider — ekranı kazımadan. API
 * yalnız destekleyen tarayıcılarda var; yoksa hiçbir şey yapılmaz. Yeni
 * sürümler `document.modelContext`, eskiler `navigator.modelContext` sunuyor.
 */
import { fetchDiscover, fetchStory, searchStories } from './api/content';
import type { Story } from './types';

type ToolResult = { content: { type: 'text'; text: string }[]; isError?: boolean };
type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean };
  execute: (input: Record<string, unknown>) => Promise<ToolResult>;
};
type ModelContext = { registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => unknown };

const TOPICS = ['business', 'data-ai', 'finance', 'growth', 'people', 'entrepreneurship', 'leadership', 'operations', 'engineering', 'marketing', 'product', 'design', 'sales'];
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function text(t: string, isError = false): ToolResult {
  return { content: [{ type: 'text', text: t }], ...(isError ? { isError } : {}) };
}

function list(stories: Story[]): string {
  if (stories.length === 0) return 'No stories found.';
  return stories
    .map((s, i) => `${i + 1}. ${s.heading} (id: ${s.id})${s.dek ? `\n   ${s.dek}` : ''}\n   ${location.origin}/s/${s.id}`)
    .join('\n');
}

function limitOf(v: unknown, def = 10): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(30, Math.max(1, Math.floor(n))) : def;
}

let controller: AbortController | null = null;

/** `navigate` router'dan gelir: araçlar tam sayfa yenilemeden gezinir. */
export function registerWebMcpTools(navigate: (to: string) => unknown, contentLang: () => string): void {
  const doc = document as unknown as { modelContext?: ModelContext };
  const nav = navigator as unknown as { modelContext?: ModelContext };
  const mc = doc.modelContext ?? nav.modelContext;
  if (!mc || typeof mc.registerTool !== 'function') return;

  controller?.abort();
  controller = new AbortController();

  const tools: WebMcpTool[] = [
    {
      name: 'search_stories',
      description: 'Search Brifing news stories by keywords in the headline (every keyword must match). Returns headlines, summaries, ids and links.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Keywords, e.g. "Nvidia earnings"' },
          limit: { type: 'integer', minimum: 1, maximum: 30, default: 10 },
        },
        required: ['query'],
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const q = String(input.query ?? '').trim();
        if (q.length < 2) return text('query must be at least 2 characters', true);
        return text(list(await searchStories(q, contentLang(), limitOf(input.limit))));
      },
    },
    {
      name: 'get_latest_stories',
      description: 'Get the newest stories on Brifing across all topics, with summaries, ids and links.',
      inputSchema: { type: 'object', properties: { limit: { type: 'integer', minimum: 1, maximum: 30, default: 10 } } },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const data = await fetchDiscover(contentLang(), 60);
        return text(list(data.stories.slice(0, limitOf(input.limit))));
      },
    },
    {
      name: 'read_story',
      description:
        'Read a Brifing story in full (all summary cards, publisher, original article link). Pass a story id; omit it to read the story currently open on the page.',
      inputSchema: { type: 'object', properties: { id: { type: 'string', description: 'Story id (UUID). Optional on a story page.' } } },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const id = String(input.id ?? '').match(UUID)?.[0] ?? location.pathname.match(/^\/s\/([^/]+)/)?.[1]?.match(UUID)?.[0];
        if (!id) return text('No story id given and no story is open.', true);
        const d = await fetchStory(id, contentLang());
        if (!d) return text(`No story with id ${id}.`, true);
        const cards = d.cards
          .filter((c) => c.kind === 'cover' || c.kind === 'body')
          .map((c) => [c.heading, c.body].filter(Boolean).join('\n'))
          .join('\n\n');
        return text(`${cards}\n\nPublisher: ${d.source?.title ?? 'unknown'}\nOriginal article: ${d.content.url}\nOn Brifing: ${location.origin}/s/${id}`);
      },
    },
    {
      name: 'open_story',
      description: 'Open a Brifing story on this page by its id.',
      inputSchema: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] },
      async execute(input) {
        const id = String(input.id ?? '').match(UUID)?.[0];
        if (!id) return text('id must be a story UUID', true);
        navigate(`/s/${id}`);
        return text(`Opened ${location.origin}/s/${id}`);
      },
    },
    {
      name: 'open_topic',
      description: `Open a topic feed on Brifing. Topics: ${TOPICS.join(', ')}.`,
      inputSchema: { type: 'object', properties: { topic: { type: 'string', enum: TOPICS } }, required: ['topic'] },
      async execute(input) {
        const topic = String(input.topic ?? '');
        if (!TOPICS.includes(topic)) return text(`Unknown topic. Use one of: ${TOPICS.join(', ')}`, true);
        navigate(`/c/${topic}`);
        return text(`Opened ${location.origin}/c/${topic}`);
      },
    },
  ];

  for (const tool of tools) {
    const execute = tool.execute;
    // Ağ hataları ajana araç hatası olarak dönsün, sayfada yakalanmamış istisna olmasın.
    tool.execute = async (input) => {
      try {
        return await execute(input ?? {});
      } catch {
        return text('Brifing could not complete this request. Try again shortly.', true);
      }
    };
    try {
      const r = mc.registerTool(tool, { signal: controller.signal }) as Promise<unknown> | undefined;
      if (r && typeof (r as Promise<unknown>).catch === 'function') (r as Promise<unknown>).catch(() => {});
    } catch {
      // Eski bir uygulama seçenek nesnesini kabul etmeyebilir: imzasız dene.
      try {
        mc.registerTool(tool);
      } catch {
        /* desteklenmiyor */
      }
    }
  }
}

